import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';

export const COUNTRIES = [
  { code: 'US', name: 'United States',   dial: '+1',    flag: '🇺🇸', minDigits: 10, maxDigits: 10, placeholder: '(555) 000-0000' },
  { code: 'CA', name: 'Canada',          dial: '+1',    flag: '🇨🇦', minDigits: 10, maxDigits: 10, placeholder: '(555) 000-0000' },
  { code: 'MX', name: 'Mexico',          dial: '+52',   flag: '🇲🇽', minDigits: 10, maxDigits: 10, placeholder: '55 1234 5678' },
  { code: 'GB', name: 'United Kingdom',  dial: '+44',   flag: '🇬🇧', minDigits: 10, maxDigits: 10, placeholder: '07700 900000' },
  { code: 'AU', name: 'Australia',       dial: '+61',   flag: '🇦🇺', minDigits: 9,  maxDigits: 9,  placeholder: '0400 000 000' },
  { code: 'AE', name: 'UAE',             dial: '+971',  flag: '🇦🇪', minDigits: 9,  maxDigits: 9,  placeholder: '50 000 0000' },
  { code: 'SA', name: 'Saudi Arabia',    dial: '+966',  flag: '🇸🇦', minDigits: 9,  maxDigits: 9,  placeholder: '50 000 0000' },
  { code: 'DE', name: 'Germany',         dial: '+49',   flag: '🇩🇪', minDigits: 10, maxDigits: 11, placeholder: '1512 3456789' },
  { code: 'FR', name: 'France',          dial: '+33',   flag: '🇫🇷', minDigits: 9,  maxDigits: 9,  placeholder: '6 12 34 56 78' },
  { code: 'IN', name: 'India',           dial: '+91',   flag: '🇮🇳', minDigits: 10, maxDigits: 10, placeholder: '98765 43210' },
  { code: 'BR', name: 'Brazil',          dial: '+55',   flag: '🇧🇷', minDigits: 10, maxDigits: 11, placeholder: '11 91234-5678' },
  { code: 'CO', name: 'Colombia',        dial: '+57',   flag: '🇨🇴', minDigits: 10, maxDigits: 10, placeholder: '310 123 4567' },
  { code: 'AR', name: 'Argentina',       dial: '+54',   flag: '🇦🇷', minDigits: 10, maxDigits: 10, placeholder: '11 1234-5678' },
  { code: 'NG', name: 'Nigeria',         dial: '+234',  flag: '🇳🇬', minDigits: 10, maxDigits: 10, placeholder: '801 234 5678' },
  { code: 'ZA', name: 'South Africa',    dial: '+27',   flag: '🇿🇦', minDigits: 9,  maxDigits: 9,  placeholder: '71 234 5678' },
  { code: 'OTHER', name: 'Other',        dial: '',      flag: '🌍', minDigits: 6,  maxDigits: 15, placeholder: 'Phone number' },
];

function digitsOnly(str) {
  return str.replace(/\D/g, '');
}

export default function PhoneInput({ value, onChange, error, label = 'Cell Phone', required = false, testId = 'phone-input' }) {
  const [country, setCountry] = useState(COUNTRIES[0]); // USA default
  const [rawNumber, setRawNumber] = useState('');
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  // Build full value whenever country or number changes
  useEffect(() => {
    const digits = digitsOnly(rawNumber);
    const full = country.dial ? `${country.dial}${digits}` : digits;
    onChange(full, digits, country);
  }, [rawNumber, country]); // eslint-disable-line

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const digits = digitsOnly(rawNumber);
  const isShort = touched && digits.length > 0 && digits.length < country.minDigits;
  const isEmpty = touched && digits.length === 0;
  const isValid = digits.length >= country.minDigits && digits.length <= country.maxDigits;

  const filtered = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.dial.includes(search)
  );

  const inputCls = `
    flex-1 px-4 py-3 rounded-r-xl text-white text-sm placeholder:text-white/35
    bg-black/50 outline-none transition-colors duration-200
    border-t border-r border-b border-white/15
    ${(isShort || isEmpty || error) ? 'border-red-500/50' : ''}
    ${isValid && touched ? 'border-[#C9A84C]/40' : ''}
    focus:border-[#C9A84C]/50 focus:shadow-[0_0_0_3px_rgba(201,168,76,0.12)]
  `;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-medium text-white/70">
          {label} {required && <span style={{ color: '#C9A84C' }}>*</span>}
        </label>
        {isValid && touched && (
          <span className="flex items-center gap-1 text-[10px] font-mono-accent" style={{ color: '#C9A84C' }}>
            <CheckCircle size={10} /> Looks good
          </span>
        )}
      </div>

      <div className="flex relative" ref={dropdownRef}>
        {/* Country selector */}
        <button
          type="button"
          onClick={() => { setOpen(!open); setSearch(''); }}
          className="flex items-center gap-1.5 px-3 py-3 rounded-l-xl border border-white/15 bg-black/50 text-sm text-white/80 hover:bg-white/5 transition-colors flex-shrink-0"
          style={{ minWidth: 88 }}
        >
          <span className="text-base leading-none">{country.flag}</span>
          <span className="text-xs text-white/60">{country.dial || '+'}</span>
          <ChevronDown size={12} className={`text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {/* Number input */}
        <input
          type="tel"
          value={rawNumber}
          onChange={(e) => setRawNumber(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={country.placeholder}
          className={inputCls}
          data-testid={testId}
          autoComplete="tel-national"
        />

        {/* Dropdown */}
        {open && (
          <div
            className="absolute top-full left-0 z-50 mt-1 rounded-xl border border-white/15 overflow-hidden"
            style={{ background: '#141415', boxShadow: '0 16px 48px rgba(0,0,0,0.7)', width: 260 }}
          >
            <div className="p-2 border-b border-white/8">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="w-full px-3 py-2 rounded-lg text-xs text-white placeholder:text-white/30 outline-none border border-white/10 bg-black/40 focus:border-[#C9A84C]/30"
                autoFocus
              />
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => { setCountry(c); setOpen(false); setSearch(''); setRawNumber(''); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/5 transition-colors"
                  style={{ background: c.code === country.code ? 'rgba(201,168,76,0.08)' : 'transparent' }}
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="text-xs text-white/80 flex-1">{c.name}</span>
                  <span className="text-xs font-mono-accent text-white/40">{c.dial}</span>
                  {c.code === country.code && <CheckCircle size={12} style={{ color: '#C9A84C' }} />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Validation messages */}
      {isEmpty && (
        <p className="text-xs mt-1 text-red-400">Phone number is required</p>
      )}
      {isShort && !isEmpty && (
        <div className="flex items-start gap-1.5 mt-1.5 p-2 rounded-lg border border-yellow-500/25 bg-yellow-500/5">
          <AlertCircle size={13} className="text-yellow-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-yellow-300 leading-relaxed">
            Please verify this number — {country.name} numbers need {country.minDigits} digits.
            You entered {digits.length}. Double-check and try again.
          </p>
        </div>
      )}
      {error && !isShort && !isEmpty && (
        <p className="text-xs mt-1 text-red-400">{error}</p>
      )}
    </div>
  );
}
